/**
 * OpenAICredentialModal — paste-token form for the user's OpenAI API
 * key, stored in workspace-credentials and read at deploy time by the
 * Codex provider gate (resolveAgentCredential with provider='codex' in
 * backend/src/handlers/apps.js).
 *
 * Uses the app's shared <MktAlertModal> (the same `mkt-modal` shell the
 * app deploy/preview modal + every other marketplace dialog use) for the
 * chrome — overlay, title, X close. The form (description + API key input
 * + Save) lives in its children. White PillButton Save, no Cancel.
 *
 * onConnected({ suffix }) — caller refreshes "now using …abc123" affordances.
 */

import React, { useEffect, useState } from 'react';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import MktAlertModal from '../molecules/MktAlertModal/MktAlertModal';
import PillButton from '../shared/PillButton';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
};

export default function OpenAICredentialModal({ isOpen, onClose, onConnected }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) { setToken(''); setError(''); setSubmitting(false); }
  }, [isOpen]);

  const handleSubmit = async () => {
    const trimmed = token.trim();
    if (!trimmed) { setError('API key is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await WorkspaceCredentials.add({ type: 'api', provider: 'openai', token: trimmed });
      onConnected?.({ suffix: res?.credential?.masked || '' });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save OpenAI API key. Please check the key and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MktAlertModal
      open={isOpen}
      onClose={onClose}
      title="Connect OpenAI"
      ariaLabel="Connect OpenAI"
      secondaryLabel=""
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
        Zibby needs an OpenAI API key to deploy apps with the Codex agent. Keys
        are KMS-encrypted at rest and only decrypted inside your per-instance
        Fargate task. Mint one at{' '}
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: 'var(--ion-color-primary)' }}
        >
          platform.openai.com/api-keys
        </a>.
      </p>
      <label htmlFor="openai-cred-token" style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>
        API key
      </label>
      <input
        id="openai-cred-token"
        type="password"
        value={token}
        placeholder="sk-…"
        disabled={submitting}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => { setToken(e.target.value); setError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !submitting && token.trim()) { e.preventDefault(); handleSubmit(); } }}
        style={inputStyle}
      />
      {error && <p role="alert" style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>{error}</p>}
      <div style={{ marginTop: 16 }}>
        <PillButton onClick={handleSubmit} disabled={submitting || !token.trim()} loading={submitting} className="btn-fullwidth">
          Save
        </PillButton>
      </div>
    </MktAlertModal>
  );
}
